import { useEffect, useRef } from "react";
import { PermissionsAndroid, Platform } from "react-native";

import * as ExpoCar from "$modules/expo-car";
import useApi from "$src/core/hooks/use-api";
import useTranslation from "$src/core/hooks/use-translation";
import type { TranslationKey } from "$src/core/models/translation";
import useStorefrontQuery from "$src/core/queries/use-storefront-query";
import useWatchlistQuery from "$src/core/queries/use-watchlist-query";
import Alerting from "$src/core/services/alerting";
import type { AlertId } from "$src/core/services/alerting/types";
import Car, { isCarPlaySupported } from "$src/core/services/car/car";
import {
  setCarRuntime,
  setCarUiMounted,
} from "$src/core/services/car/runtime/runtime";
import runCarTask from "$src/core/services/car/task/task";
import runAndroidCarTask from "$src/core/services/car/task/task.androidauto";
import Logging from "$src/core/services/logging/logging";

type Translate = (key: TranslationKey) => string;

/** POST_NOTIFICATIONS did not exist as a runtime permission before API 33. */
const ANDROID_NOTIFICATION_PERMISSION_API = 33;

/** The media3 car transport notification needs this grant. Android-only; callers must check. */
const requestNotificationPermission = async (): Promise<void> => {
  if (Number(Platform.Version) < ANDROID_NOTIFICATION_PERMISSION_API) return;

  const permission = PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS;
  if (await PermissionsAndroid.check(permission)) return;

  await PermissionsAndroid.request(permission);
};

/** React-side car glue: connect toasts, runtime refresh, task re-runs on fresher catalog data. */
const useCarIntegration = () => {
  const { api } = useApi();
  const { t } = useTranslation();

  const { data: storefront } = useStorefrontQuery();
  const { data: watchlist } = useWatchlistQuery();

  /* One prompt per app run, however often a car reconnects. */
  const notificationPermissionRequested = useRef(false);

  /* The "connected" toast stays up until disconnect dismisses it by id. */
  const connectedToastIdRef = useRef<AlertId>(undefined);

  /* Keep translate current for long-lived car listeners without re-subscribing. */
  const translateRef = useRef<Translate>(t);
  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  /* Keep the injected car runtime in sync with the mounted tree. */
  useEffect(() => {
    setCarRuntime({ api, translate: t });
  }, [api, t]);

  /* Let the tasks know the phone UI exists, which gates their error toasts. */
  useEffect(() => {
    setCarUiMounted(true);
    return () => setCarUiMounted(false);
  }, []);

  /* iOS: toast while connected; the toast never times out, so disconnect must clear it. */
  useEffect(() => {
    if (!isCarPlaySupported) return undefined;

    const connectSub = ExpoCar.addConnectListener(() => {
      Alerting.dismiss(connectedToastIdRef.current);
      connectedToastIdRef.current = Alerting.success(
        translateRef.current("CAR/CONNECTED"),
      );
    });
    const disconnectSub = ExpoCar.addDisconnectListener(() => {
      Alerting.dismiss(connectedToastIdRef.current);
      connectedToastIdRef.current = undefined;
    });

    return () => {
      connectSub.remove();
      disconnectSub.remove();
    };
  }, []);

  /* iOS: rebuild the CarPlay root on catalog/label change; also heals a cold launch that rooted empty. */
  useEffect(() => {
    if (!isCarPlaySupported) return;
    if (!storefront && !watchlist) return;
    if (!ExpoCar.isConnected()) return;

    runCarTask({ storefront, watchlist }).catch((error: unknown) => {
      Logging.error("CarPlay task failed", { error });
    });
  }, [storefront, watchlist, t]);

  /* Android: toast on connect and request the permission media3 needs. */
  useEffect(() => {
    if (isCarPlaySupported || !Car.isSupported) return undefined;

    const connectedSubscription = Car.addConnectedListener(({ connected }) => {
      Alerting.dismiss(connectedToastIdRef.current);
      connectedToastIdRef.current = undefined;
      if (!connected) return;

      connectedToastIdRef.current = Alerting.success(
        translateRef.current("CAR/CONNECTED"),
      );

      if (notificationPermissionRequested.current) return;
      notificationPermissionRequested.current = true;
      requestNotificationPermission().catch((error: unknown) => {
        Logging.error("Could not request notification permission", { error });
      });
    });

    return () => {
      connectedSubscription.remove();
    };
  }, []);

  /* Android: republish the catalog on fresher data, also with no car attached, to keep the cold-start snapshot fresh. */
  useEffect(() => {
    if (isCarPlaySupported || !Car.isSupported) return;
    if (!storefront && !watchlist) return;

    runAndroidCarTask({ storefront, watchlist }).catch((error: unknown) => {
      Logging.error("Android Auto task failed", { error });
    });
  }, [storefront, watchlist, t]);
};

export default useCarIntegration;
